import { useState } from "react";
import { Action, Card } from "../../calm.tsx";
import { ErrorNote, inputCls } from "../../ui.tsx";
import type { SkillFolderDTO } from "./skills.ts";

/** Add from a GitHub URL, a skills.sh entry or a zip. When the source holds several skills, `choose` lists them and the admin ticks which to add. */
export function SkillAddForm({
  initialSource = "", choose, busy, error, onFetch, onCancel, onSourceChange,
}: {
  initialSource?: string;
  /** Called when the source text or the zip changes, so the parent drops the folder list found for the old source. */
  onSourceChange?: () => void;
  choose?: SkillFolderDTO[];
  busy?: boolean;
  error?: unknown;
  onFetch: (input: { source?: string; zip?: File; folders?: string[] }) => void;
  onCancel: () => void;
}) {
  const [source, setSource] = useState(initialSource);
  const [zip, setZip] = useState<File | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const folders = choose?.length ? picked : undefined;
  const can = (source.trim() !== "" || zip !== null) && (!choose?.length || picked.length > 0);
  return (
    <Card className="space-y-4">
      <form
        className="space-y-4"
        data-testid="skill-add-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (can) onFetch(zip ? { zip, folders } : { source: source.trim(), folders });
        }}
      >
        <label className="block text-[12.5px] text-ink-2">
          GitHub URL or skills.sh entry
          <input className={`${inputCls} mt-1.5 font-mono`} value={source} onChange={(e) => { setSource(e.target.value); setPicked([]); onSourceChange?.(); }} disabled={zip !== null} placeholder="https://github.com/owner/repo or owner/repo@skill" data-testid="skill-source-input" />
        </label>
        <label className="block text-[12.5px] text-ink-2">
          …or a zip
          <input type="file" accept=".zip,application/zip" className="mt-1.5 block text-[13px]" onChange={(e) => { setZip(e.target.files?.[0] ?? null); setPicked([]); onSourceChange?.(); }} data-testid="skill-zip-input" />
        </label>
        {choose && choose.length > 0 && (
          <fieldset data-testid="skill-folder-picker">
            <legend className="text-[12.5px] text-ink-2">This source holds {choose.length} skills. Pick which to add.</legend>
            <ul className="mt-2 space-y-1.5">
              {choose.map((f) => (
                <li key={f.path}>
                  <label className="flex cursor-pointer items-center gap-2 text-[13px]">
                    <input type="checkbox" checked={picked.includes(f.path)} onChange={(e) => setPicked((cur) => (e.target.checked ? [...cur, f.path] : cur.filter((p) => p !== f.path)))} data-testid={`skill-folder-${f.name}`} />
                    <span className="font-mono">{f.name}</span>
                    <span className="text-ink-3">{f.path}</span>
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
        )}
        <ErrorNote error={error} />
        <div className="flex gap-2">
          <Action primary type="submit" disabled={!can || busy} testId="skill-fetch">{choose?.length ? "Add picked skills" : "Fetch"}</Action>
          <Action onClick={onCancel} testId="skill-add-cancel">Cancel</Action>
        </div>
        <p className="text-[12px] text-ink-3">Nothing is executed. New skills start as pending and need your review.</p>
      </form>
    </Card>
  );
}
